import { Component, EventEmitter, Input, Output } from '@angular/core';
import { Router } from '@angular/router';
import { ConfirmationSimpleComponent } from '@lib/components/confirmation-simple/confirmation-simple.component';
import { isActeNatureOperation, NatureOperationId } from '@lib/models/graphqlData';
import { FicheConseilState } from '@lib/models/generated/graphql';
import { OuiDialogService } from 'omnium-ui/dialog';
import { OuiSnackbarService } from 'omnium-ui/snackbar';
import { MenuNode } from 'omnium-ui/shared';
import { FicheConseilTableItem } from 'src/app/components/my-fiches/fiche-conseil-dashboard/fiche-conseil-table/fiche-conseil-table.component';
import { RenommerFicheDialogComponent } from 'src/app/components/my-fiches/renommer-fiche-dialog/renommer-fiche-dialog.component';
import { ConseilConfigService } from 'src/app/services/conseil-config.service';
import { FicheConseilService } from 'src/app/services/fiche-conseil.service';

@Component({
  selector: 'app-fiche-conseil-table-more-action',
  templateUrl: './fiche-conseil-table-more-action.component.html',
  styleUrls: ['./fiche-conseil-table-more-action.component.scss'],
})
export class FicheConseilTableMoreActionComponent {
  @Input({ required: true }) row: FicheConseilTableItem;
  menuItems: MenuNode[] = [];
  canCreateFromSource = false;

  @Output() onReloadRequired = new EventEmitter<void>();

  constructor(
    private ficheConseilService: FicheConseilService,
    private router: Router,
    private dialogService: OuiDialogService,
    private conseilConfigService: ConseilConfigService,
    private snackbarService: OuiSnackbarService
  ) {}

  async ngOnInit() {
    await this.setMenu();
  }

  async setMenu() {
    try {
      this.canCreateFromSource =
        this.row.natureOperationId === NatureOperationId.ArbitrageLibre &&
        (await this.conseilConfigService.getValidityStatusByConfigId(this.row.conseilConfigId)) === 'valid';
    } catch {
      this.canCreateFromSource = false;
    }

    this.menuItems = [
      {
        name: 'Modifier',
        action: () => {
          this.router.navigate([isActeNatureOperation(this.row.natureOperationId) ? 'acte' : 'fiche', this.row.id]);
        },
        disabled: false,
      },
      {
        name: 'Renommer',
        action: () => {
          const modalRef = this.dialogService.openDialog(
            RenommerFicheDialogComponent,
            {
              ficheId: this.row.id,
            },
            'auto',
            '621px'
          );
          modalRef.afterClosed().subscribe(success => {
            if (success) {
              this.onReloadRequired.emit();
            }
          });
        },
        disabled: false,
      },
      {
        name: 'Voir le document',
        action: () => {
          this.ficheConseilService.displayPDF(this.row.id);
        },
        disabled: this.row.statut !== FicheConseilState.ConseilDone,
      },
      {
        name: 'Télécharger',
        action: () => {
          this.ficheConseilService.downloadPDF(this.row.id, this.row.label);
        },
        disabled: this.row.statut !== FicheConseilState.ConseilDone,
      },
      ...(this.canCreateFromSource
        ? [
            {
              name: 'Créer à partir de',
              action: async () => {
                const newFicheId = await this.ficheConseilService.createArbitrageFicheFromSource(this.row.id);
                if (newFicheId) {
                  this.router.navigate(['acte', newFicheId]);
                  return;
                }

                this.snackbarService.open(
                  'Une erreur est survenue lors de la création de la fiche préremplie',
                  'error',
                  5000,
                  {
                    horizontal: 'left',
                    vertical: 'bottom',
                  }
                );
              },
              disabled: false,
            },
          ]
        : []),
      {
        name: 'Supprimer',
        action: async () => {
          const modalRef = this.dialogService.openDialog(
            ConfirmationSimpleComponent,
            {
              title: 'Supprimer la fiche conseil',
              message:
                'Vous êtes sur le point de supprimer une fiche conseil. Toute suppression est définitive. Votre saisie, et le document éventuellement déjà généré dans ce parcours seront supprimés.',
              validateButtonLabel: 'Supprimer',
              cancelButtonLabel: 'Annuler',
            },
            'auto',
            '434px'
          );
          modalRef.afterClosed().subscribe((isValidated: boolean) => {
            if (isValidated) {
              this.ficheConseilService.deleteFiches([this.row.id]).then(() => {
                this.onReloadRequired.emit();
              });
            }
          });
        },
        disabled: false,
      },
    ];
  }
}
